import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { money, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Wallet, Pencil, Trash2, ArrowLeftRight } from "lucide-react";

const emptyAcct = () => ({ name: "", provider: "", type: "business", opening_balance: "" });
const emptyTransfer = () => ({ date: new Date().toISOString().slice(0, 10), from_account: "cash", to_account: "", amount: "", kind: "cash_deposit", note: "" });

export const BankAccountsView = ({ filterType, title, subtitle }) => {
  const [accounts, setAccounts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAcct());
  const [tOpen, setTOpen] = useState(false);
  const [tForm, setTForm] = useState(emptyTransfer());

  const load = useCallback(async () => {
    const [a, t] = await Promise.all([api.get("/bank-accounts"), api.get("/transfers")]);
    setAccounts(a.data);
    setTransfers(t.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = accounts.filter((a) => (filterType ? a.type === filterType : true));
  const nameOf = (id) => id === "cash" ? "Cash on hand" : (accounts.find((a) => a.id === id)?.name || "—");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setT = (k, v) => setTForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm({ ...emptyAcct(), type: filterType || "business" }); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ name: a.name, provider: a.provider || "", type: a.type, opening_balance: String(a.opening_balance || "") }); setOpen(true); };

  const save = async () => {
    if (!form.name) return;
    const payload = { name: form.name, provider: form.provider, type: form.type, opening_balance: parseFloat(form.opening_balance) || 0 };
    if (editing) await api.put(`/bank-accounts/${editing.id}`, payload);
    else await api.post("/bank-accounts", payload);
    toast.success(editing ? "Account updated" : "Account added");
    setOpen(false); load();
  };
  const remove = async (id) => { if (!window.confirm("Delete this account? Linked transactions will be untagged.")) return; await api.delete(`/bank-accounts/${id}`); toast.success("Removed"); load(); };

  const saveTransfer = async () => {
    const amount = parseFloat(tForm.amount) || 0;
    if (!amount || !tForm.to_account || !tForm.from_account) { toast.error("Pick both accounts and an amount"); return; }
    if (tForm.from_account === tForm.to_account) { toast.error("Pick two different accounts"); return; }
    await api.post("/transfers", { ...tForm, amount });
    toast.success("Movement recorded");
    setTOpen(false); setTForm(emptyTransfer()); load();
  };
  const removeTransfer = async (id) => { if (!window.confirm("Delete this transfer?")) return; await api.delete(`/transfers/${id}`); load(); };

  const KIND = { transfer: "Transfer", cash_deposit: "Cash deposit", drawing: "Owner drawing" };
  const acctOptions = [{ id: "cash", name: "Cash on hand" }, ...accounts];

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setTForm(emptyTransfer()); setTOpen(true); }} data-testid="record-transfer-btn" className="gap-2">
            <ArrowLeftRight className="w-4 h-4" /> Transfer / deposit
          </Button>
          <Button onClick={openNew} data-testid="add-account-btn" className="gap-2"><Plus className="w-4 h-4" /> Add account</Button>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card px-5 py-12 text-center" data-testid="accounts-empty">
          <Wallet className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No accounts yet. Add one per provider/account (e.g. NAB Business, CBA Savings).</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {shown.map((a) => (
            <div key={a.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`account-card-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-semibold truncate">{a.name}</div>
                  <div className="text-xs text-muted-foreground">{a.provider || "—"} · <span className="capitalize">{a.type}</span></div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(a)} data-testid={`edit-account-${a.id}`} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(a.id)} data-testid={`delete-account-${a.id}`} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">Balance</div>
                <div className="num text-2xl font-bold" data-testid={`account-balance-${a.id}`}>{money(a.balance)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {transfers.length > 0 && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 bg-secondary/50 font-heading font-semibold text-sm">Transfers & deposits</div>
          <div className="divide-y divide-border">
            {transfers.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-4 py-3 text-sm row-hover" data-testid={`transfer-row-${t.id}`}>
                <div className="min-w-0">
                  <div className="font-medium">{KIND[t.kind] || "Transfer"}: {nameOf(t.from_account)} → {nameOf(t.to_account)}</div>
                  <div className="text-xs text-muted-foreground num">{fmtDate(t.date)}{t.note ? ` · ${t.note}` : ""}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="num font-semibold">{money(t.amount)}</span>
                  <button onClick={() => removeTransfer(t.id)} data-testid={`delete-transfer-${t.id}`} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit account" : "New account"}</DialogTitle>
            <DialogDescription>Add a bank account by provider and individual account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nickname</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="account-name" placeholder="NAB Business" /></div>
              <div><Label>Provider</Label><Input value={form.provider} onChange={(e) => set("provider", e.target.value)} className="mt-1.5" data-testid="account-provider" placeholder="NAB" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="account-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Opening balance</Label><Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => set("opening_balance", e.target.value)} className="mt-1.5" data-testid="account-opening" placeholder="0.00" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-account-btn">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent className="sm:max-w-md" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Transfer / deposit</DialogTitle>
            <DialogDescription>Move money between accounts — no effect on income, expenses or GST.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div>
              <Label>Type</Label>
              <Select value={tForm.kind} onValueChange={(v) => setT("kind", v)}>
                <SelectTrigger className="mt-1.5" data-testid="transfer-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash_deposit">Cash deposit (bank your takings)</SelectItem>
                  <SelectItem value="transfer">Transfer between accounts</SelectItem>
                  <SelectItem value="drawing">Owner drawing to personal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From</Label>
                <Select value={tForm.from_account} onValueChange={(v) => setT("from_account", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="transfer-from"><SelectValue placeholder="From" /></SelectTrigger>
                  <SelectContent>{acctOptions.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>To</Label>
                <Select value={tForm.to_account} onValueChange={(v) => setT("to_account", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="transfer-to"><SelectValue placeholder="To" /></SelectTrigger>
                  <SelectContent>{acctOptions.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount</Label><Input type="number" step="0.01" value={tForm.amount} onChange={(e) => setT("amount", e.target.value)} className="mt-1.5" data-testid="transfer-amount" placeholder="0.00" /></div>
              <div><Label>Date</Label><Input type="date" value={tForm.date} onChange={(e) => setT("date", e.target.value)} className="mt-1.5" data-testid="transfer-date" /></div>
            </div>
            <div><Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={tForm.note} onChange={(e) => setT("note", e.target.value)} className="mt-1.5" data-testid="transfer-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTOpen(false)}>Cancel</Button>
            <Button onClick={saveTransfer} data-testid="save-transfer-btn">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function BankAccountsPage() {
  return <BankAccountsView filterType="business" title="Bank Accounts" subtitle="Your business accounts across providers, with live balances." />;
}
